// ============================================================================
// CaseHoldPanel — right-pane panel for the "Hold History" sidebar item
// ============================================================================
// Three layers:
//   1. Current-state card — either the active hold record or "Case is active".
//   2. Action button row — Hold (when active) or Resume (when held), each
//      gated by the existing permissions.
//   3. History list — full HOLD/RESUME audit log, most recent first.

import React, { useCallback, useEffect, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import {
  useCurrentCaseHold,
} from 'hooks/api/useCaseClosure';
import { caseHoldService } from 'api/services/caseClosureService';
import type { LitigationCase } from 'api/types/litigationCase.types';
import type { CaseHoldHistoryEntry } from 'api/types/caseHold.types';
import type { useRecsuitPermissions } from 'hooks/useRecsuitPermissions';
import HoldCaseModal from '../components/HoldCaseModal';
import ResumeCaseModal from '../components/ResumeCaseModal';

export interface CaseHoldPanelProps {
  kase: LitigationCase;
  permissions: ReturnType<typeof useRecsuitPermissions>;
  onMutated: () => void;
}

const CaseHoldPanel: React.FC<CaseHoldPanelProps> = ({ kase, permissions, onMutated }) => {
  const { data: current, loading, refetch } = useCurrentCaseHold(kase.id);

  const [history, setHistory] = useState<CaseHoldHistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try { setHistory(await caseHoldService.getHistory(kase.id)); }
    finally { setHistoryLoading(false); }
  }, [kase.id]);

  useEffect(() => { void loadHistory(); }, [loadHistory]);

  const refresh = useCallback(async () => {
    await refetch();
    await loadHistory();
    onMutated();
  }, [refetch, loadHistory, onMutated]);

  const isClosed = kase.caseStatus === 'CLOSED';
  const isHeld   = current?.holdStatus === 'ON_HOLD';
  const canHold   = !isClosed && !isHeld
                    && permissions.hasAction('litigation-case', 'hold')
                    && !!kase.workflowInstanceId;
  const canResume = isHeld && permissions.hasAction('litigation-case', 'resume');

  const [holdOpen,   setHoldOpen]   = useState(false);
  const [resumeOpen, setResumeOpen] = useState(false);

  return (
    <div style={{ padding: '0.25rem 0' }}>
      <h3 style={headerStyle}>
        <IconSystem.clock size={18} style={{ marginRight: '0.45rem', verticalAlign: '-3px' }} />
        Hold History
      </h3>

      {/* Current state card */}
      {loading ? (
        <div style={mutedBlock}>Loading…</div>
      ) : isHeld && current ? (
        <ActiveHoldCard hold={current} />
      ) : (
        <div style={emptyCardStyle}>
          <div style={{ fontWeight: 500, marginBottom: '0.25rem' }}>
            {isClosed ? 'Case is closed' : 'Case is active'}
          </div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
            {isClosed
              ? 'Closed cases cannot be held. Reopen the case first.'
              : 'Not currently on hold. Use Hold Case to pause workflow transitions.'}
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div style={{ marginTop: '0.85rem', display: 'flex', gap: '0.5rem' }}>
        {canHold && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setHoldOpen(true)}
          >
            <IconSystem.clock size={14} style={{ marginRight: '0.4rem' }} />
            Hold Case
          </button>
        )}
        {canResume && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setResumeOpen(true)}
          >
            <IconSystem.arrowRight size={14} style={{ marginRight: '0.4rem' }} />
            Resume Case
          </button>
        )}
        {!canHold && !canResume && !isClosed && (
          <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
            {!kase.workflowInstanceId
              ? 'The case must be claimed (workflow started) before hold.'
              : `You don't have permission to ${isHeld ? 'resume' : 'hold'} this case.`}
          </span>
        )}
      </div>

      {/* History */}
      <h4 style={{ ...headerStyle, fontSize: '0.85rem', marginTop: '1.5rem' }}>History</h4>
      {historyLoading ? (
        <div style={mutedBlock}>Loading history…</div>
      ) : history.length === 0 ? (
        <div style={mutedBlock}>No hold or resume events yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {history.map((h) => <HistoryRow key={h.id} h={h} />)}
        </div>
      )}

      <HoldCaseModal
        isOpen={holdOpen}
        caseId={kase.id}
        caseCode={kase.caseCode}
        onClose={() => setHoldOpen(false)}
        onHeld={refresh}
      />
      <ResumeCaseModal
        isOpen={resumeOpen}
        caseId={kase.id}
        caseCode={kase.caseCode}
        onClose={() => setResumeOpen(false)}
        onResumed={refresh}
      />
    </div>
  );
};

// ----------------------------------------------------------------------------

const ActiveHoldCard: React.FC<{ hold: { holdReasonName?: string | null; heldAt?: string | null; expectedResumeAt?: string | null } }> = ({ hold }) => (
  <div style={{
    padding: '0.85rem 1rem',
    border: '1px solid #fde68a',
    borderLeft: '4px solid #d97706',
    borderRadius: 6,
    background: '#fef3c7',
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
      <IconSystem.clock size={16} color="#92400e" />
      <strong style={{ fontSize: '0.9rem', color: '#78350f' }}>On Hold</strong>
      {hold.holdReasonName && (
        <span style={{
          padding: '1px 8px', borderRadius: '999px',
          fontSize: '0.7rem', fontWeight: 600,
          background: 'white', color: '#78350f',
        }}>{hold.holdReasonName}</span>
      )}
    </div>
    <div style={{ fontSize: '0.78rem', color: '#78350f' }}>
      {hold.heldAt && <>Since <strong>{new Date(hold.heldAt).toLocaleString('en-IN')}</strong></>}
      {hold.expectedResumeAt && (
        <> · Expected resume <strong>{new Date(hold.expectedResumeAt).toLocaleDateString('en-IN')}</strong></>
      )}
    </div>
    <div style={{ marginTop: '0.4rem', fontSize: '0.72rem', color: '#92400e' }}>
      Workflow transitions are refused by the engine while held.
    </div>
  </div>
);

const HistoryRow: React.FC<{ h: CaseHoldHistoryEntry }> = ({ h }) => {
  const isHold = h.action === 'HOLD';
  return (
    <div style={{
      padding: '0.6rem 0.85rem',
      border: '1px solid var(--border-color)',
      borderRadius: 4,
      background: 'var(--bg-primary)',
      fontSize: '0.8rem',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.2rem' }}>
        <span style={{
          padding: '1px 8px', borderRadius: '999px',
          fontSize: '0.65rem', fontWeight: 600,
          background: isHold ? '#fef3c7' : '#dcfce7',
          color: isHold ? '#78350f' : '#166534',
        }}>{h.action}</span>
        {isHold && h.holdReasonName && <strong>{h.holdReasonName}</strong>}
      </div>
      <div style={{ color: 'var(--text-secondary)', fontSize: '0.72rem' }}>
        {new Date(h.actedAt).toLocaleString('en-IN')} by {h.actedByName}
        {h.sourceStateCode ? ` · from state ${h.sourceStateCode}` : ''}
        {isHold && h.expectedResumeAt && (
          <> · Expected resume {new Date(h.expectedResumeAt).toLocaleDateString('en-IN')}</>
        )}
      </div>
      {h.remarks && (
        <div style={{ marginTop: '0.3rem', fontStyle: 'italic', color: 'var(--text-primary)' }}>
          "{h.remarks}"
        </div>
      )}
    </div>
  );
};

const headerStyle: React.CSSProperties = {
  fontSize: '1rem', fontWeight: 600, margin: '0 0 0.6rem',
  paddingBottom: '0.3rem', borderBottom: '1px solid var(--border-color)',
};

const emptyCardStyle: React.CSSProperties = {
  padding: '0.85rem 1rem',
  border: '1px dashed var(--form-border-color)',
  borderRadius: 6,
  background: 'var(--bg-secondary)',
};

const mutedBlock: React.CSSProperties = {
  padding: '0.65rem 0.85rem',
  fontSize: '0.78rem',
  color: 'var(--text-secondary)',
  fontStyle: 'italic',
};

export default CaseHoldPanel;
